import { z } from "zod";
import { feePricingSnapshotSchema } from "./fees.js";
import { feePricingDataSchema } from "./txs.js";
import { dateStringSchema } from "./common.js";
import { chainReorgSchema } from "./health.js";
export const blockHistoryPointSchema = z.object({
    blockNumber: z.number(),
    timestamp: z.number().nullable(),
    slotNumber: z.number().nullable(),
    numTxs: z.number(),
    totalFees: z.string().nullable(),
    totalManaUsed: z.string().nullable(),
    feePerDaGas: z.string().nullable(),
    feePerL2Gas: z.string().nullable(),
    coinbase: z.string().nullable(),
    checkpointNumber: z.number().nullable(),
    indexWithinCheckpoint: z.number().nullable(),
    cpStatus: z.enum(["checkpointed", "proven", "finalized"]).nullable(),
    cpBlockCount: z.number().nullable(),
    cpAttestations: z.number().nullable(),
    pricing: feePricingSnapshotSchema.nullable().optional(),
});
export const blockHistoryResponseSchema = z.object({
    data: z.array(blockHistoryPointSchema),
});
export const blockHistoryQuerySchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    limit: z.string().optional(),
});
export const blockStatsProposerSchema = z.object({
    coinbase: z.string().nullable(),
    blockCount: z.number(),
    share: z.number(),
});
export const blockStatsDataSchema = z.object({
    blockCount: z.number(),
    blockRange: z.object({ from: z.number(), to: z.number() }),
    timespan: z.number(),
    avgBlockTime: z.number(),
    avgTxsPerBlock: z.number(),
    maxTxsPerBlock: z.number(),
    totalTxs: z.number(),
    avgManaPerBlock: z.string(),
    maxManaPerBlock: z.string(),
    avgFeesPerBlock: z.string(),
    totalFees: z.string(),
    emptyBlocks: z.number(),
    emptyBlockPct: z.number(),
    proposerCount: z.number(),
    missedSlots: z.number(),
    proposers: z.array(blockStatsProposerSchema),
});
export const blockStatsResponseSchema = z.object({
    data: blockStatsDataSchema.nullable(),
});
export const blockStatsQuerySchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
});
/**
 * Recorded rollup prunes, newest first.
 *
 * A dedicated endpoint rather than a field on the paginated blocks response: the blocks chart needs
 * every prune overlapping its window regardless of which page of blocks is loaded, and a future
 * banner needs the same list without asking for blocks at all.
 */
export const reorgsResponseSchema = z.object({
    data: z.array(chainReorgSchema),
    /**
     * The individual slots inside those prunes whose work was discarded, within the queried range.
     *
     * Sent as slot numbers rather than left for the client to infer from `orphanFirstSlot` /
     * `orphanLastSlot`, because a prune's span is not uniformly orphaned: on 2026-08-14 it covered 66
     * slots of which 62 were mined-then-discarded and four failed on their own account (three
     * `checkpoint-valid`, one `blocks-missed`). Treating the span as one block made the blocks chart
     * label those four as pruned while the epoch timeline, which applies the per-slot rule, correctly
     * showed them as missed — two surfaces disagreeing about the same slot. The rule lives in
     * `isOrphanedSlot` and only its output crosses the wire.
     */
    orphanedSlots: z.array(z.number()),
});
export const reorgsQuerySchema = z.object({
    /** Optional slot window; a prune is returned when its discarded span overlaps it at all. */
    fromSlot: z.coerce.number().optional(),
    toSlot: z.coerce.number().optional(),
});
export const blockConfigDataSchema = z.object({
    maxL2BlockGas: z.number(),
    aztecSlotDuration: z.number(),
    aztecEpochDuration: z.number(),
});
export const blockConfigResponseSchema = z.object({
    data: blockConfigDataSchema.nullable(),
});
export const checkpointStatsDataSchema = z.object({
    checkpointCount: z.number(),
    range: z.object({ from: z.number(), to: z.number() }),
    avgBlocksPerCheckpoint: z.number(),
    maxBlocksPerCheckpoint: z.number(),
    avgManaPerCheckpoint: z.string(),
    avgFeesPerCheckpoint: z.string(),
    avgAttestations: z.number(),
    provenCount: z.number(),
    finalizedCount: z.number(),
    provenPct: z.number(),
    finalizedPct: z.number(),
});
export const checkpointStatsResponseSchema = z.object({
    data: checkpointStatsDataSchema.nullable(),
});
export const checkpointStatsQuerySchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    fromBlock: z.string().optional(),
    toBlock: z.string().optional(),
});
// ── Block Detail ──
export const blockNumberParamSchema = z.object({
    id: z.string(),
    blockNumber: z.string(),
});
export const blockDetailTxSchema = z.object({
    txHash: z.string(),
    txIndex: z.number().nullable(),
    status: z.string(),
    blockNumber: z.number().nullable(),
    feePayer: z.string(),
    actualFee: z.string().nullable(),
    executionResult: z.string().nullable(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numContractClassLogs: z.number(),
    numPublicLogs: z.number().nullable(),
    numPublicDataWrites: z.number().nullable(),
    gasLimitDa: z.number().nullable(),
    gasLimitL2: z.number().nullable(),
    maxFeePerDaGas: z.number().nullable(),
    maxFeePerL2Gas: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    expirationTimestamp: z.number().nullable(),
});
export const blockCheckpointSchema = z.object({
    checkpointNumber: z.number(),
    slotNumber: z.number().nullable(),
    startBlock: z.number().nullable(),
    endBlock: z.number().nullable(),
    blockCount: z.number(),
    attestationCount: z.number().nullable(),
    provenAt: dateStringSchema.nullable(),
    finalizedAt: dateStringSchema.nullable(),
    l1BlockNumber: z.number().nullable(),
    l1Timestamp: z.number().nullable(),
});
export const blockDetailResponseSchema = z.object({
    block: z.object({
        blockNumber: z.number(),
        blockHash: z.string().nullable(),
        timestamp: z.number().nullable(),
        slotNumber: z.number().nullable(),
        numTxs: z.number(),
        totalFees: z.string().nullable(),
        totalManaUsed: z.string().nullable(),
        feePerDaGas: z.string().nullable(),
        feePerL2Gas: z.string().nullable(),
        coinbase: z.string().nullable(),
        feeRecipient: z.string().nullable(),
        checkpointNumber: z.number().nullable(),
        indexWithinCheckpoint: z.number().nullable(),
    }),
    checkpoint: blockCheckpointSchema.nullable(),
    transactions: z.array(blockDetailTxSchema),
    previousBlockTimestamp: z.number().nullable(),
    nextBlockTimestamp: z.number().nullable(),
    manaUtilizationPct: z.number().nullable(),
    feePricing: feePricingDataSchema.nullable(),
});
//# sourceMappingURL=blocks.js.map